import * as React from "react";
import { cn } from "@/v2/lib/utils";
import {
  formatHHmm,
  nextDaySuffix,
  setTranslucentDragImage,
  snapMinutes,
  toMinutes,
} from "../schedulerUtils";
import type { DragPayload, Program } from "../types";
import type { TouchDragHandle } from "../useTouchDragBridge";
import { colorStyle } from "./parts";

/** Card width (px) below which the compact two-line time layout is used. */
const NARROW_WIDTH = 130;
/** The narrowest a block is drawn, so a very short clip stays visible. */
const MIN_BLOCK_WIDTH = 4;
/** Below this a block shows no text, only its colour and a tooltip. */
const SLIVER_WIDTH = 36;

interface Props {
  program: Program;
  /** Minutes between the strip's origin and this program's day midnight
   * (dayIndex * 1440), so a block on a later day renders further right. */
  offsetMin?: number;
  selected: boolean;
  height: number;
  pixelsPerHour: number;
  dragRef: React.MutableRefObject<DragPayload | null>;
  touchDrag?: TouchDragHandle;
  onSelect: (id: string) => void;
  /** Drag one edge of the block to trim it in place; omitted where resizing
   *  doesn't apply (e.g. WeekGrid's compact tiles). */
  onResizeEdge?: (id: string, edge: "start" | "end", deltaMin: number) => void;
  /** How far the edges may stretch (strip-axis minutes): flush against the
   *  neighbouring blocks or the strip walls, so a handle dragged into a
   *  neighbour stops at its boundary instead of overlapping it. */
  resizeBounds?: { minStart: number; maxEnd: number };
}

/** A grip on the block's left/right edge; drag it to trim that side in
 *  place instead of moving the whole block. Native HTML5 drag (the block's
 *  own draggable=true, used to move it) is a poor fit for a live-resizing
 *  gesture, so this tracks the pointer directly instead. */
function ResizeHandle({
  edge,
  onDrag,
}: {
  edge: "start" | "end";
  onDrag: (deltaMin: number, committing: boolean) => void;
}) {
  return (
    <div
      role="presentation"
      aria-hidden
      draggable={false}
      data-testid={`resize-${edge}`}
      className={cn(
        "absolute inset-y-0 z-10 flex w-3 cursor-ew-resize items-center justify-center",
        edge === "start" ? "left-0" : "right-0",
      )}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        const startClientX = e.clientX;
        const move = (ev: PointerEvent) => onDrag(ev.clientX - startClientX, false);
        const up = (ev: PointerEvent) => {
          document.removeEventListener("pointermove", move);
          document.removeEventListener("pointerup", up);
          onDrag(ev.clientX - startClientX, true);
        };
        document.addEventListener("pointermove", move);
        document.addEventListener("pointerup", up);
      }}
    >
      <div
        className="rounded-full bg-white/90"
        style={{ width: 3, height: 20, boxShadow: "0 0 0 1px rgba(0,0,0,0.15)" }}
      />
    </div>
  );
}

/** One program on the timeline, positioned and sized from its start/end.
 * An end past 1440 minutes simply renders wider, carrying the block across
 * the midnight boundary into the next day's region of the strip. */
export function ProgramBlock({
  program,
  offsetMin = 0,
  selected,
  height,
  pixelsPerHour,
  dragRef,
  touchDrag,
  onSelect,
  onResizeEdge,
  resizeBounds,
}: Props) {
  const ppm = pixelsPerHour / 60;
  const start = offsetMin + toMinutes(program.startTime);
  const end = offsetMin + toMinutes(program.endTime);
  // While a handle is being dragged, this previews the resized edge locally;
  // nothing commits to the schedule until pointerup.
  const [resizePreview, setResizePreview] = React.useState<{ start: number; end: number } | null>(
    null,
  );
  const previewStart = resizePreview?.start ?? start;
  const previewEnd = resizePreview?.end ?? end;
  const durationMin = previewEnd - previewStart;
  // Never below a visible sliver: a negative CSS width (a one minute block
  // at the day scale is 1.3px minus the 4px gutter) is dropped by the
  // browser, which then sizes the block to its text, drawing a one minute
  // clip about two hours wide.
  const width = Math.max(MIN_BLOCK_WIDTH, durationMin * ppm - 4);
  // A block under about an hour and a half is too narrow to fit the times on
  // one line, so it gets tighter padding and the times stacked on two lines.
  const narrow = width < NARROW_WIDTH;
  // Too narrow for any text: just a marker, with the details in a tooltip;
  // zooming in gives it room.
  const sliver = width < SLIVER_WIDTH;

  // The edges stop flush at the neighbouring blocks (resizeBounds) and at
  // the content's own limits: the start can reach back only as far as the
  // trimmed-off head allows (trim in stays >= 0) and never before its own
  // day's midnight; the end can reach only to the content's real runtime.
  const minStart = Math.max(
    resizeBounds?.minStart ?? 0,
    offsetMin,
    start - Math.floor(program.trimInSec / 60),
  );
  const maxEnd = Math.min(
    resizeBounds?.maxEnd ?? Number.POSITIVE_INFINITY,
    start + Math.max(1, Math.floor((program.durationSec - program.trimInSec) / 60)),
  );

  const handleResizeDrag = (edge: "start" | "end") => (deltaPx: number, committing: boolean) => {
    const deltaMin = snapMinutes(deltaPx / ppm);
    if (edge === "end") {
      const newEnd = Math.min(maxEnd, Math.max(start + 1, end + deltaMin));
      if (committing) {
        setResizePreview(null);
        if (newEnd !== end) onResizeEdge?.(program.id, "end", newEnd - end);
      } else {
        setResizePreview({ start, end: newEnd });
      }
    } else {
      const newStart = Math.min(end - 1, Math.max(minStart, start + deltaMin));
      if (committing) {
        setResizePreview(null);
        if (newStart !== start) onResizeEdge?.(program.id, "start", newStart - start);
      } else {
        setResizePreview({ start: newStart, end });
      }
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      draggable={!program.pendingDuration}
      aria-pressed={selected}
      aria-label={`${program.title}, ${formatHHmm(program.startTime)} to ${formatHHmm(program.endTime)}${nextDaySuffix(program.endTime)}`}
      data-testid={`program-${program.id}`}
      title={sliver ? `${program.title}, ${formatHHmm(program.startTime)} to ${formatHHmm(program.endTime)}${nextDaySuffix(program.endTime)}` : undefined}
      onClick={() => onSelect(program.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(program.id);
        }
      }}
      onDragStart={(e) => {
        if (program.pendingDuration) {
          e.preventDefault();
          return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        dragRef.current = {
          kind: "program",
          id: program.id,
          durationMin,
          grabOffsetMin: (e.clientX - rect.left) / ppm,
        };
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", program.id);
        setTranslucentDragImage(e, e.currentTarget as HTMLElement);
      }}
      onDragEnd={() => (dragRef.current = null)}
      {...(program.pendingDuration
        ? {}
        : touchDrag?.bind(
            (touch, rect) => ({
              kind: "program",
              id: program.id,
              durationMin,
              grabOffsetMin: (touch.clientX - rect.left) / ppm,
            }),
            program.title,
          ))}
      className={cn(
        "absolute cursor-grab overflow-hidden rounded-md border text-foreground active:cursor-grabbing",
        selected && "ring-2 ring-action",
        // Waiting on the exact runtime: visibly parked and not draggable.
        program.pendingDuration && "cursor-wait opacity-50 grayscale",
      )}
      style={{
        ...colorStyle(program.color),
        left: previewStart * ppm + 2,
        width,
        top: 8,
        height: height - 16,
        boxSizing: "border-box",
        padding: sliver ? 0 : narrow ? "6px 5px" : "8px 8px",
      }}
    >
      {sliver ? null : (
      <>
      <div
        className="font-semibold leading-tight"
        style={{
          fontSize: narrow ? 11 : 12,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
        }}
      >
        {program.title}
      </div>
      <div
        className="leading-tight text-muted-foreground"
        style={{ fontSize: narrow ? 11 : 12, marginTop: 4, whiteSpace: narrow ? "nowrap" : undefined }}
      >
        {narrow ? (
          <>
            <span style={{ display: "block" }}>{formatHHmm(program.startTime)} -</span>{" "}
            <span style={{ display: "block" }}>
              {formatHHmm(program.endTime)}
              {nextDaySuffix(program.endTime)}
            </span>
          </>
        ) : (
          <>
            {formatHHmm(program.startTime)} - {formatHHmm(program.endTime)}
            {nextDaySuffix(program.endTime)}
          </>
        )}
      </div>
      </>
      )}
      {onResizeEdge && !program.pendingDuration && selected && !sliver ? (
        <>
          <ResizeHandle edge="start" onDrag={handleResizeDrag("start")} />
          <ResizeHandle edge="end" onDrag={handleResizeDrag("end")} />
        </>
      ) : null}
    </div>
  );
}
